import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import AttendanceAccordion from './AttendanceAccordion';
import ContractorNoticeAccordion from './ContractorNoticeAccordion';
import ProfileTab from './ProfileTab';
import UserSettingsAccordion from './UserSettingsAccordion';
import IconButton from 'components/@extended/IconButton';
import LogoIcon from 'components/logo/LogoIcon';
import ThemeModeSwitch from 'components/ThemeModeSwitch';
import useAuth from 'hooks/useAuth';
import {
  setSharedClockState,
  refreshSharedClockState,
  useSharedClockState,
  refreshSharedOpenPropustka,
  useSharedOpenPropustka
} from 'hooks/useAttendanceClockState';
import { notifyAttendanceChanged } from 'hooks/useAttendanceRefresh';
import { clockAttendance, getAttendanceState, getVerificationReasons, listPropustkaReasons } from 'api/attendance';
import { resolveAssetUrl } from 'utils/assetUrl';
import { formatPhoneDisplay, formatPhoneHref } from 'utils/formatPhone';
import { hasModuleAccess } from 'utils/roleWeights';
import { verificationReasonIconFor } from 'utils/verificationReasonIcons';
import useExclusiveAccordion from 'utils/useExclusiveAccordion';

// assets
import { IconAlarmOff, IconAlarmOn, IconBadge, IconClose, IconLocation, IconLogout, IconMail, IconPhone, IconPropustka, IconUser } from 'utils/icons';

// ==============================|| HEADER CONTENT - PROFILE (OFFCANVAS-RIGHT-USER) ||============================== //
// Hlavička (2026-08-29, na výslovné přání uživatele) - obrázek v levé části
// hlavičky (max 3/4 šířky), se třemi úrovněmi fallbacku:
//   1. `user.photo_url` - fotka ZAMĚSTNANCE. Zatím NEEXISTUJE nikde v API
//      (core_users nemá sloupec na fotku) - jen připravený layout pro
//      budoucí funkci (viz project memory "employee-photo-upload-proposal").
//   2. `user.tenant_logo_url` - logo KLIENTA (core_tenants.logo_url, migrace
//      054, TenantRepository::logoUrlFor(), AuthController::me()). Reálné a
//      funkční - první nasazený příklad je THERMOTECHNIKA BOHEMIA
//      (public/tenant-logos/tc-brand.png). NULL, dokud admin logo klientovi
//      nenastaví (zatím jen přímo v DB, žádné UI na "Klienti" stránce).
//   3. `LogoIcon` - obecná DO-RA značka (bez tenanta ani fotky, typicky
//      superadmin/lokální core účty), když nemá ani jedno z výše.
//
// Reálný bug fixed (2026-08-29): `tenant_logo_url`/`photo_url` v DB jsou
// ULOŽENÉ jako cesty relativní ke kořeni Vite `public/` adresáře, ale appka
// běží pod `base` prefixem - bez `resolveAssetUrl()` (utils/assetUrl.js)
// appka žádala "/tenant-logos/..." místo "/app/tenant-logos/...", dostala
// 404 a prohlížeč místo obrázku ukázal jen `alt` text.

// Duplicitní s AttendanceAccordion.jsx's stejnojmenné funkce - vědomě,
// stejný princip jako "vlastní, samostatný stav" mezi touhle rychlou
// hlavičkovou akcí a plným accordionem níže (viz AttendanceAccordion.jsx's
// docblock).
function tryGetPosition(timeoutMs = 4000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    const timer = setTimeout(() => resolve(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { timeout: timeoutMs }
    );
  });
}

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const displayName = user ? `${user.first_name} ${user.last_name}`.trim() : '';

  // Viz komentář u ContractorNoticeAccordion níže - přímé členství ve
  // skupině "externí dodavatelé" bez zároveň "zaměstnanci" přepne
  // AttendanceAccordion na mnohem menší ContractorNoticeAccordion.
  const groupNames = (user?.groups || []).map((g) => (g.name || '').trim().toLowerCase());
  const isContractorOnly = groupNames.includes('externí dodavatelé') && !groupNames.includes('zaměstnanci');

  const [open, setOpen] = useState(false);
  const handleToggle = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Vzájemné zavírání top-level accordionů v offcanvasu (2026-08-29, na
  // výslovné přání uživatele - "pokud je otevřený accordion Nastavení
  // uživatele, tak by se měl uzavřít při otevření dalšího accordionu") -
  // stav zvednutý sem, oba accordiony (AttendanceAccordion,
  // UserSettingsAccordion) dostávají `expanded`/`onChange` jako controlled
  // props místo svého dřívějšího nezávislého uncontrolled stavu.
  const [expandedDrawerSection, handleDrawerSectionChange] = useExclusiveAccordion();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Rychlé píchnutí přímo v hlavičce, vedle fotky (2026-08-29, na výslovné
  // přání uživatele) - stejná akce jako "Odchod nebo příchod" v
  // AttendanceAccordion.jsx, teď i se stejným chováním ("modal buď s
  // úspěchem nebo bez úspěchu s doplněním, to platí u všech tlačítek u
  // docházky") - klik otevře modal, který rovnou zkusí píchnutí zaznamenat;
  // pokud appka potřebuje důvod (ATT-400-001, chybějící GPS/firemní síť),
  // dořeší se přímo v tomhle modalu, ne přesměrováním do accordionu níže.
  const canSeeAttendance = hasModuleAccess(user?.permissions, 'attendance', 'reader');
  // Sdílený "external store" (2026-08-29, real bug fixed - "u všech
  // tlačítek... je potřeba sledovat stav z jakéhokoliv tlačítka na
  // příslušnou akci") - viz hooks/useAttendanceClockState.js's docblock.
  // Píchnutí přes AttendanceAccordion.jsx (nebo naopak) se teď propíše i
  // sem, okamžitě, bez zavření a znovuotevření offcanvasu.
  const clockState = useSharedClockState();
  // Stejný sdílený store, teď i pro "mám otevřenou propustku" (2026-08-31,
  // na výslovné přání uživatele - "PROBÍHÁ PROPUSTKA" vedle fotky místo
  // Jste/Nejste v práci, dokud propustka trvá).
  const openPropustka = useSharedOpenPropustka();
  const [clockModalOpen, setClockModalOpen] = useState(false);
  const [clockBusy, setClockBusy] = useState(false);
  const [clockError, setClockError] = useState('');
  const [clockSuccess, setClockSuccess] = useState('');
  const [needsReason, setNeedsReason] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [selectedReasonIds, setSelectedReasonIds] = useState([]);

  useEffect(() => {
    if (!canSeeAttendance) return;
    refreshSharedClockState();
    refreshSharedOpenPropustka();
    // Prefetch (2026-08-31, na výslovné přání uživatele - "načíst to s
    // menu") - Profile se mountuje jednou s hlavičkou, ne opakovaně jako
    // AttendanceAccordion.jsx uvnitř Draweru, takže tenhle fetch stihne
    // doběhnout dřív, než uživatel otevře dialog Propustky. Výsledek jde do
    // sdílené cache v api/attendance.js, druhé volání uvnitř
    // AttendanceAccordion.jsx ho jen znovu použije.
    listPropustkaReasons().catch(() => {});
  }, [canSeeAttendance]);

  const nextClockAction = clockState === 'clock_in' ? 'clock_out' : 'clock_in';
  const isAtWork = clockState === 'clock_in';

  const handleQuickClock = async (reasonIds) => {
    setClockBusy(true);
    setClockError('');
    setClockSuccess('');
    try {
      const position = await tryGetPosition();
      await clockAttendance('standard', undefined, {
        latitude: position?.latitude,
        longitude: position?.longitude,
        verification_reason_ids: reasonIds && reasonIds.length > 0 ? reasonIds : undefined
      });
      const d = await getAttendanceState();
      setSharedClockState(d.state);
      setClockSuccess('Zaznamenáno.');
      setNeedsReason(false);
      setSelectedReasonIds([]);
      notifyAttendanceChanged();
    } catch (e) {
      if (e.code === 'ATT-400-001') {
        try {
          const fresh = await getAttendanceState();
          setSharedClockState(fresh.state);
          const freshNextAction = fresh.state === 'clock_in' ? 'clock_out' : 'clock_in';
          setReasons(await getVerificationReasons(freshNextAction));
        } catch {
          setReasons([]);
        }
        setNeedsReason(true);
      } else {
        setClockError(e.message || 'Píchnutí se nepodařilo zaznamenat.');
      }
    } finally {
      setClockBusy(false);
    }
  };

  const openClockModal = () => {
    setClockError('');
    setClockSuccess('');
    setNeedsReason(false);
    setSelectedReasonIds([]);
    setClockModalOpen(true);
    handleQuickClock([]);
  };

  const toggleReason = (id) => {
    setSelectedReasonIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title="Profil" disableInteractive>
        <IconButton
          sx={{ color: 'text.primary', bgcolor: open ? 'grey.100' : 'transparent' }}
          aria-label="otevřít profil"
          aria-controls={open ? 'offcanvas-right-user' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          color="secondary"
          variant="light"
        >
          <IconUser style={{ fontSize: '1.65rem' }} />
        </IconButton>
      </Tooltip>
      <Drawer
        id="offcanvas-right-user"
        anchor="right"
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 320 }, display: 'flex', flexDirection: 'column' } } }}
      >
        {/* 2026-08-29, na výslovné přání uživatele - přestavěno z "3 sloupců"
            (foto / text / křížek vedle sebe v jedné řadě, text se pak mačkal
            do zbytku místa) na 2 sloupce (foto ~40 %, text ~60 % šířky
            offcanvasu) + křížek ABSOLUTNĚ umístěný do rohu, mimo tok obou
            sloupců - `pr` na obalu mu dopředu vyhradí místo, aby se do něj
            text nikdy nezalomil "pod tlačítko". Vlastní `Stack` kolem
            křížku (ne holý IconButton) počítá s dalšími tlačítky pod ním. */}
        <Box sx={{ position: 'relative', pl: 2.5, pr: 6, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack sx={{ position: 'absolute', top: 12, right: 12, gap: 0.5 }}>
            <IconButton onClick={handleClose} aria-label="zavřít">
              <IconClose style={{ fontSize: '1.4rem' }} />
            </IconButton>
          </Stack>

          <Stack direction="row" sx={{ gap: 1.5, alignItems: 'stretch' }}>
            <Box sx={{ width: '40%', flexShrink: 0 }}>
              {user?.photo_url ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    width: '100%',
                    aspectRatio: '1 / 1',
                    p: 0.5,
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    boxSizing: 'border-box'
                  }}
                >
                  <Box
                    component="img"
                    src={resolveAssetUrl(user.photo_url)}
                    alt={displayName}
                    sx={{ width: '100%', height: '100%', borderRadius: 0.5, objectFit: 'cover' }}
                  />
                </Box>
              ) : user?.tenant_logo_url ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    width: '100%',
                    aspectRatio: '1 / 1',
                    p: 0.5,
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    boxSizing: 'border-box'
                  }}
                >
                  <Box
                    component="img"
                    src={resolveAssetUrl(user.tenant_logo_url)}
                    alt="Logo klienta"
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'inline-flex',
                    width: '100%',
                    aspectRatio: '1 / 1',
                    p: 0.5,
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    boxSizing: 'border-box'
                  }}
                >
                  <LogoIcon />
                </Box>
              )}
            </Box>

            <Stack sx={{ width: '60%', minWidth: 0 }}>
              {/* fontSize natvrdo menší než subtitle1 (2026-08-29, na
                  výslovné přání uživatele - "když tam bude 18 znaků, ať se
                  vejde") - 60% sloupec je na šířku offcanvasu (320px) úzký,
                  16px písmo by se do něj s 18 znaky nevešlo na jeden řádek;
                  wordBreak zůstává jako pojistka pro cokoliv delšího. */}
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}>
                {user?.first_name}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}>
                {user?.last_name}
              </Typography>

              {/* Dvě rychlá tlačítka pod jménem/příjmením, vedle fotky
                  (2026-08-29, na výslovné přání uživatele) - píchnutí
                  (střídá se příchod/odchod, stejná logika jako
                  AttendanceAccordion) a odhlášení, obojí jedním klikem
                  přímo z hlavičky. `flex: 1` na tomhle řádku ho natáhne až
                  ke spodní hraně fotky (díky `alignItems: 'stretch'` na
                  řádku foto+text výše); `mr` stejně jako e-mail/telefon níže
                  ho natáhne až k pravému okraji offcanvasu. Docházka má
                  `flex: 2` (dvojnásobek), odhlášení `flex: 1` - na výslovné
                  přání uživatele "zvětši jen to od docházky", ne rovný půl
                  na půl jako předtím. */}
              <Stack direction="row" sx={{ gap: 1, flex: 1, mt: 1, mr: -3.5 }}>
                {canSeeAttendance && (
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{
                      flex: 2,
                      '& .MuiBadge-badge': {
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '50%',
                        p: 0.4,
                        minWidth: 0,
                        height: 'auto'
                      }
                    }}
                    badgeContent={
                      /* Badge ukazuje AKTUÁLNÍ stav (jsem v práci/nejsem),
                         ne akci tlačítka (2026-08-29, na výslovné přání
                         uživatele) - zelený AlarmOn = clock_state je
                         'clock_in', jinak červený AlarmOff. Nezávislé na
                         tom, co tlačítko udělá po kliknutí (nextClockAction).
                         `color` prop místo CSS proměnné (real bug fixed -
                         `var(--mui-palette-success-main)` v tomhle projektu
                         není definovaná, appka nepoužívá MUI CSS-variables
                         theming, takže ikona zůstávala bez barvy). */
                      openPropustka ? (
                        <IconPropustka color="warning" style={{ fontSize: '0.85rem' }} />
                      ) : isAtWork ? (
                        <IconAlarmOn color="success" style={{ fontSize: '0.85rem' }} />
                      ) : (
                        <IconAlarmOff color="error" style={{ fontSize: '0.85rem' }} />
                      )
                    }
                  >
                    <ButtonBase
                      onClick={openClockModal}
                      disabled={clockBusy}
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: 1,
                        border: 1,
                        borderColor: openPropustka ? 'warning.main' : isAtWork ? 'success.main' : 'error.main'
                      }}
                    >
                      {/* Dvouřádkový text, menší písmo (2026-08-29, na
                          výslovné přání uživatele) - "JSTE / V PRÁCI" nebo
                          "NEJSTE / V PRÁCI", dokud netrvá propustka - pak
                          "PROBÍHÁ / PROPUSTKA" (2026-08-31, na výslovné
                          přání uživatele). Po ukončení propustky se vrátí na
                          Jste/Nejste v práci samo, podle toho, čím byla
                          propustka ukončena - žádná zvláštní logika navíc
                          tady není potřeba, jen sdílený openPropustka store
                          se vynuluje. */}
                      <Stack sx={{ alignItems: 'center', lineHeight: 1.15 }}>
                        {openPropustka ? (
                          <>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Probíhá</Typography>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Propustka</Typography>
                          </>
                        ) : (
                          <>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>
                              {isAtWork ? 'Jste' : 'Nejste'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>V práci</Typography>
                          </>
                        )}
                      </Stack>
                    </ButtonBase>
                  </Badge>
                )}
                <Tooltip title="Odhlásit se">
                  <ButtonBase
                    onClick={handleLogout}
                    sx={{ flex: 1, borderRadius: 1, border: 1, borderColor: 'divider', color: 'error.main' }}
                  >
                    <IconLogout style={{ fontSize: '1.3rem' }} />
                  </ButtonBase>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>

          {/* Funkce (job_title) a oblast (department) POD celou řádkou foto
              + jméno (2026-08-29, oprava - "posuň funkci pod fotku"), přes
              celou šířku offcanvasu stejně jako e-mail/telefon níže. */}
          {/* textWrap: 'balance' (2026-08-29, na výslovné přání uživatele) -
              místo ručního počítání znaků a hledání nejbližší mezery
              prohlížeč sám rozdělí delší text na řádky co nejvíc vyrovnaně
              (ne "první řádek nacpaný, druhý jedno slovo"). Nepodporující
              prohlížeč prostě spadne na běžné zalomení - nic se nerozbije. */}
          {/* marginTop: '2px' na ikonách (2026-08-29, real bug fixed -
              "ikony nejsou uprostřed prvního řádku, zdají se výš") -
              `alignItems: 'flex-start'` zarovná ikonu k úplnému hornímu
              okraji Stacku, ale Typography má kolem textu ještě neviditelný
              padding daný jeho line-height, takže samotný text (viditelné
              písmenka) začíná o pár px níž než horní okraj řádku - bez
              nudge byla ikona opticky výš než text vedle ní. */}
          {user?.job_title && (
            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 0.75, mt: 1.5, mr: -3.5, color: 'text.secondary' }}>
              <IconBadge style={{ fontSize: '1rem', flexShrink: 0, marginTop: '2px' }} />
              <Typography sx={{ fontSize: '0.9rem', color: 'inherit', wordBreak: 'break-word', textWrap: 'balance' }}>{user.job_title}</Typography>
            </Stack>
          )}

          {user?.department && (
            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 0.75, mt: 0.5, mr: -3.5, color: 'text.secondary' }}>
              <IconLocation style={{ fontSize: '1rem', flexShrink: 0, marginTop: '2px' }} />
              <Typography sx={{ fontSize: '0.9rem', color: 'inherit', wordBreak: 'break-word', textWrap: 'balance' }}>{user.department}</Typography>
            </Stack>
          )}

          {/* Oddělovací čára pod funkcí/oblastí (2026-08-29, na výslovné
              přání uživatele) - `mr` stejně jako e-mail/telefon níže sahá
              přes celou šířku offcanvasu, ne jen do 60% sloupce. */}
          <Divider sx={{ mt: 1.5, mr: -3.5 }} />

          {/* E-mail pod fotkou/textem, přes celou šířku offcanvasu
              (2026-08-29, na výslovné přání uživatele), stejná velikost
              písma jako jméno/příjmení výše. `mr` záporně vyrovnává `pr: 6`
              obalu (vyhrazené pro křížek nahoře) - tady dole už křížku
              nepřekáží, tak ať e-mail reálně sahá na standardní okraj (2.5),
              ne jen do zúžené šířky sloupců výše. */}
          <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 0.75, mt: 1.5, mr: -3.5, color: 'text.secondary' }}>
            <IconMail style={{ fontSize: '1rem', flexShrink: 0, marginTop: '2px' }} />
            <Typography sx={{ fontSize: '0.9rem', color: 'inherit', wordBreak: 'break-word' }}>{user?.identifier}</Typography>
          </Stack>

          {/* Telefon pod e-mailem (2026-08-29, na výslovné přání uživatele) -
              jen když ho appka zná (core_users.phone se u pozvaných účtů
              často nevyplňuje, prázdný řádek by nic neříkal). */}
          {user?.phone && (
            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 0.75, mt: 0.5, mr: -3.5, color: 'text.secondary' }}>
              <IconPhone style={{ fontSize: '1rem', flexShrink: 0, marginTop: '2px' }} />
              <Typography
                component="a"
                href={`tel:${formatPhoneHref(user.phone)}`}
                sx={{ fontSize: '0.9rem', color: 'inherit', textDecoration: 'none', wordBreak: 'break-word' }}
              >
                {formatPhoneDisplay(user.phone)}
              </Typography>
            </Stack>
          )}
        </Box>

        {/* Modal pro rychlé píchnutí (2026-08-29, na výslovné přání
            uživatele - "modal buď s úspěchem nebo bez úspěchu s doplněním").
            Stejný vzor jako AttendanceAccordion.jsx's "Odchod nebo příchod"
            dialog, jen vlastní/duplicitní stav. */}
        <Dialog open={clockModalOpen} onClose={() => setClockModalOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {nextClockAction === 'clock_in' ? 'Příchod' : 'Odchod'}
            <IconButton size="small" onClick={() => setClockModalOpen(false)} aria-label="zavřít">
              <IconClose style={{ fontSize: '1.3rem' }} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack sx={{ gap: 1.25, pt: 0.5 }}>
              {clockBusy && (
                <Stack sx={{ alignItems: 'center', py: 1 }}>
                  <CircularProgress size={22} />
                </Stack>
              )}

              {!clockBusy && needsReason && (
                <Stack sx={{ gap: 1 }}>
                  <Alert severity="warning">
                    Nepodařilo se ověřit polohu ani firemní síť
                    <br />
                    vyberte alespoň jeden důvod.
                  </Alert>
                  {reasons.map((r) => {
                    const ReasonIcon = verificationReasonIconFor(r.icon);
                    return (
                      <FormControlLabel
                        key={r.id}
                        control={<Checkbox size="small" checked={selectedReasonIds.includes(r.id)} onChange={() => toggleReason(r.id)} />}
                        label={
                          <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                            {ReasonIcon && <ReasonIcon style={{ fontSize: '1rem' }} />}
                            <Typography variant="caption">{r.label}</Typography>
                          </Stack>
                        }
                      />
                    );
                  })}
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    disabled={selectedReasonIds.length === 0}
                    onClick={() => handleQuickClock(selectedReasonIds)}
                    sx={{ textTransform: 'none' }}
                  >
                    Potvrdit a zaznamenat
                  </Button>
                </Stack>
              )}

              {!clockBusy && !needsReason && clockSuccess && (
                <Stack sx={{ gap: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    {clockSuccess}
                  </Typography>
                  <Button fullWidth size="small" variant="outlined" onClick={() => setClockModalOpen(false)} sx={{ textTransform: 'none' }}>
                    Zavřít
                  </Button>
                </Stack>
              )}

              {!clockBusy && !needsReason && clockError && (
                <Stack sx={{ gap: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'error.main' }}>
                    {clockError}
                  </Typography>
                  <Stack direction="row" sx={{ gap: 1 }}>
                    <Button fullWidth size="small" variant="contained" onClick={() => handleQuickClock([])} sx={{ textTransform: 'none' }}>
                      Zkusit znovu
                    </Button>
                    <Button fullWidth size="small" variant="outlined" onClick={() => setClockModalOpen(false)} sx={{ textTransform: 'none' }}>
                      Zavřít
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Externí dodavatel (2026-08-30, na výslovné přání uživatele) -
            nepíchá, nebere propustky, nežádá o dovolenou přes schvalovací
            cyklus, jen NAHLÁSÍ volno - dostává ContractorNoticeAccordion
            MÍSTO celého AttendanceAccordion, ne SubAccordion navíc uvnitř
            něj. Stejná skupina "externí dodavatelé" jako
            RecentAttendanceRecords.jsx's "zaměstnanci" gate - přímé
            členství, ne role/oprávnění na modulu. Kdyby byl uživatel
            zároveň i "zaměstnanci" (souběh rolí appka nezakazuje), vidí
            plný AttendanceAccordion - "zaměstnanci" má přednost. */}
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          {isContractorOnly ? (
            <ContractorNoticeAccordion
              expanded={expandedDrawerSection === 'attendance'}
              onChange={handleDrawerSectionChange('attendance')}
            />
          ) : (
            <AttendanceAccordion
              onNavigate={handleClose}
              expanded={expandedDrawerSection === 'attendance'}
              onChange={handleDrawerSectionChange('attendance')}
            />
          )}
        </Box>

        {/* "Odhlásit se" (ProfileTab) přesunuto sem dolů, hned k "Nastavení
            uživatele" (2026-08-29, na výslovné přání uživatele) - dřív bylo
            hned pod hlavičkou, kde teď stejně duplikovalo jméno/e-mail, co
            už ukazuje hlavička výše. `mt: 'auto'` (dřív na UserSettingsAccordion)
            se přesunulo sem - je to teď první prvek dvojice tlačené dolů. */}
        <Box sx={{ mt: 'auto', borderTop: 1, borderColor: 'divider' }}>
          <ProfileTab />
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <UserSettingsAccordion
            expanded={expandedDrawerSection === 'settings'}
            onChange={handleDrawerSectionChange('settings')}
          />
        </Box>

        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}
        >
          <Stack sx={{ lineHeight: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 500, color: 'text.secondary' }}>
              Změna režimu
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 500, color: 'text.secondary' }}>
              světlý / tmavý / auto
            </Typography>
          </Stack>
          <ThemeModeSwitch />
        </Stack>
      </Drawer>
    </Box>
  );
}
